/**
 * Regulation filter dimensions.
 * GET /regulation-filter-dimensions
 * GET /regulation-filter-dimensions/{dimension}/values
 */

import { apiClient } from "./apiClient";

export type FilterDimensionSource = "preset" | "km_node" | "cache";
export type FilterDimensionSelection = "single" | "multi";

export interface FilterDimension {
  key: string;
  displayName: string;
  selection: FilterDimensionSelection;
  source: FilterDimensionSource;
  concept?: string;
  /** Present when the API sends it. Dual-source dimensions are false. Not used to hide dimensions. */
  sortable?: boolean;
}

export interface FilterOption {
  value: string;
  name: string;
  code?: string;
  id?: string;
}

export interface FilterValuesPage {
  items: FilterOption[];
  total: number;
  page: number;
  pageSize: number;
}

export const FILTER_VALUES_PAGE_SIZE = 50;

export const FILTER_DIMENSION_KEYS = {
  favorite: "favorite",
  publishedDate: "published_date",
  category: "category",
  healthAuthority: "health_authority",
  markets: "markets",
  productType: "product_type",
  subCategory: "sub_category",
  topics: "topics",
  tags: "tags",
} as const;

const LEADING_DIMENSION_KEYS = [
  FILTER_DIMENSION_KEYS.favorite,
  FILTER_DIMENSION_KEYS.publishedDate,
];

interface ApiFilterDimension {
  key?: string;
  display_name?: string;
  displayName?: string;
  selection?: string;
  source?: string;
  concept?: string;
  sortable?: boolean;
}

interface ApiFilterOption {
  value?: string;
  name?: string;
  code?: string;
  id?: string;
}

interface ApiValuesData {
  items?: ApiFilterOption[];
  total?: number;
  page?: number;
  page_size?: number;
}

function asSelection(value: string | undefined): FilterDimensionSelection {
  return value === "single" ? "single" : "multi";
}

function asSource(value: string | undefined): FilterDimensionSource {
  if (value === "km_node" || value === "cache" || value === "preset") return value;
  return "preset";
}

function toDimension(raw: ApiFilterDimension): FilterDimension | null {
  const key = raw.key?.trim();
  if (!key) return null;
  return {
    key,
    displayName: raw.display_name?.trim() || raw.displayName?.trim() || key,
    selection: asSelection(raw.selection),
    source: asSource(raw.source),
    concept: raw.concept?.trim() || undefined,
    sortable: raw.sortable,
  };
}

/** Favorite and Published Date stay first. Every other dimension follows A–Z. */
export function orderApiFilterDimensions(
  dimensions: FilterDimension[],
): FilterDimension[] {
  const byKey = new Map(dimensions.map((dimension) => [dimension.key, dimension]));
  const leading = LEADING_DIMENSION_KEYS.flatMap((key) => {
    const dimension = byKey.get(key);
    return dimension ? [dimension] : [];
  });
  const rest = dimensions
    .filter((dimension) => !LEADING_DIMENSION_KEYS.includes(dimension.key))
    .sort((left, right) =>
      left.displayName.localeCompare(right.displayName, undefined, {
        sensitivity: "base",
      }),
    );
  return [...leading, ...rest];
}

function toOption(raw: ApiFilterOption): FilterOption | null {
  const name = raw.name?.trim() || raw.value?.trim();
  const value = raw.value?.trim() || name;
  if (!name || !value) return null;
  return {
    value,
    name,
    code: raw.code?.trim() || undefined,
    id: raw.id?.trim() || undefined,
  };
}

function emptyValues(page: number, pageSize: number): FilterValuesPage {
  return { items: [], total: 0, page, pageSize };
}

export async function loadFilterDimensions(): Promise<FilterDimension[]> {
  try {
    const result = await apiClient.get<{ dimensions?: ApiFilterDimension[] }>(
      "/regulation-filter-dimensions",
    );
    if (result.code !== 200) return [];
    const dimensions = (result.data?.dimensions ?? [])
      .map(toDimension)
      .filter((dimension): dimension is FilterDimension => dimension != null);
    return orderApiFilterDimensions(dimensions);
  } catch {
    return [];
  }
}

export async function loadFilterDimensionValues(
  dimension: FilterDimension,
  options?: { q?: string; page?: number; pageSize?: number },
): Promise<FilterValuesPage> {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? FILTER_VALUES_PAGE_SIZE;
  const q = options?.q?.trim() || undefined;
  try {
    const result = await apiClient.get<ApiValuesData>(
      `/regulation-filter-dimensions/${encodeURIComponent(dimension.key)}/values`,
      {
        page,
        page_size: pageSize,
        ...(q ? { q } : {}),
      },
    );
    if (result.code !== 200 || !result.data) return emptyValues(page, pageSize);
    const items = (result.data.items ?? [])
      .map(toOption)
      .filter((item): item is FilterOption => item != null);
    return {
      items,
      total: result.data.total ?? items.length,
      page: result.data.page ?? page,
      pageSize: result.data.page_size ?? pageSize,
    };
  } catch {
    return emptyValues(page, pageSize);
  }
}

/** Follows pages until the dimension is exhausted. Used by the manage dialog. */
export async function loadAllFilterDimensionValues(
  dimension: FilterDimension,
): Promise<FilterOption[]> {
  const first = await loadFilterDimensionValues(dimension, { page: 1 });
  const items = [...first.items];
  let page = first.page;
  while (items.length < first.total && page < first.page + 20) {
    page += 1;
    const next = await loadFilterDimensionValues(dimension, { page });
    if (next.items.length === 0) break;
    items.push(...next.items);
  }
  return items;
}
